/**
 * A generic wrapper for the native fetch API.
 * * @param {string} endpoint - The URL to fetch (can be relative or absolute).
 * @param {object} options - Configuration options.
 * @param {string} [options.method='GET'] - HTTP method (GET, POST, PUT, DELETE).
 * @param {object} [options.body] - The data to send (automatically stringified).
 * @param {string} [options.token] - The auth token (automatically added to headers).
 * @param {object} [options.headers] - Custom headers to merge or override defaults.
 */
import { getApiBaseUrl } from "../utils/apiUtils";

export function useAPI() {
  async function callAPI(
    endpoint,
    { method = "GET", body, token, headers = {}, ...customConfig } = {},
  ) {
    // 1. Prepare Headers
    const configHeaders = {
      "Content-Type": "application/json",
      ...headers, // Allow overriding Content-Type if needed
    };

    if (token) {
      configHeaders.Authorization = `Token ${token}`;
    }

    // 2. Configure the Request
    const config = {
      method,
      headers: configHeaders,
      ...customConfig,
    };

    // 3. Handle Body (only if data is provided)
    if (body) {
      if (body instanceof FormData) {
        // IF FORMDATA: Pass directly and strip the Content-Type header
        // so the browser can automatically generate the multipart boundary
        config.body = body;
        delete configHeaders["Content-Type"];
      } else {
        // IF REGULAR OBJECT: Stringify as usual
        config.body = JSON.stringify(body);
      }
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/${endpoint}`, config);
      var requires2FA = false;
      // 4. Handle Response Errors (Fetch doesn't throw on 400-500 status)
      if (!response.ok) {
        // Check for a 2FA reverification callback
        if (response.status === 403) {
          const responseData = await response.json();
          if (responseData?.requires_2fa || responseData?.requires_2fa_setup) {
            requires2FA = true;

            // if no destination is set, the user has no device configured —
            // log them out and redirect to login to start fresh
            const codeSentTo = responseData?.code_sent_to || responseData?.mobile_number_masked;
            if (!codeSentTo || codeSentTo === "...") {
              document.cookie =
                "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
              window.location.href = "/login";
              return;
            }

            // Store the current location for redirect after 2FA
            try {
              const currentPath =
                window.location.pathname + window.location.search;
              sessionStorage.setItem("post_2fa_redirect", currentPath);

              // Get the partial auth token from the response if provided
              if (responseData.partial_auth_token) {
                sessionStorage.setItem(
                  "partial_auth_token",
                  responseData.partial_auth_token,
                );
              }
            } catch (error) {
              console.error(
                "Failed to set session storage for 2FA redirect:",
                error,
              );
            }
            const partialToken =
              responseData.partial_auth_token ||
              sessionStorage.getItem("partial_auth_token") ||
              "unknown";

            // Save destination, delivery method, and backup availability for TwoFA screen
            const dest = responseData.code_sent_to || responseData.mobile_number_masked;
            if (dest) {
              localStorage.setItem("2fa_code_sent_to", dest);
            }
            if (responseData.delivery_method) {
              localStorage.setItem("2fa_delivery_method", responseData.delivery_method);
            }
            if (responseData.email_backup_available) {
              localStorage.setItem("2fa_email_backup_available", "1");
            } else {
              localStorage.removeItem("2fa_email_backup_available");
            }

            //Redirect to appropriate 2FA page
            const doNotRedirectPages = ["/login", "/signup"];
            if (responseData?.requires_2fa_setup) {
              // Redirect to 2FA init page using window.location if not already on page
              if (
                !window.location.pathname.startsWith("/2fa_init") &&
                !doNotRedirectPages.includes(window.location.pathname)
              ) {
                window.location.href = `/2fa_init/${partialToken}`;
              }
            } else if (responseData?.requires_2fa) {
              // Redirect to 2FA verify page using window.location if not already on page
              if (
                !window.location.pathname.startsWith("/2fa_verify") &&
                !doNotRedirectPages.includes(window.location.pathname)
              ) {
                window.location.href = `/2fa_verify/${partialToken}`;
              }
            }
            throw new Error("Two-Factor Authentication required.");
          }
        }
        if (!requires2FA) {
          // Try to parse error message from server, fallback to status text

          const errorData = await response.json().catch(() => null);
          const errorMessage =
            errorData?.detail ||
            errorData?.error ||
            errorData?.message ||
            response.statusText;

          const error = new Error(errorMessage);
          error.data = errorData; // preserve full server response
          error.status = response.status;

          throw error;
        }
      }

      // 5. Handle Empty Responses & Return Parsed JSON
      // 204 No Content or a 0 Content-Length header indicate there's nothing to parse
      const isNoContent = response.status === 204;
      const responseText = await response.text();

      return isNoContent || !responseText ? null : JSON.parse(responseText);
    } catch (error) {
      // Log error or handle global error reporting here
      console.error("API Call Failed:", error);

      if (error.data?.detail === "Invalid token.") {
        document.cookie =
          "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        window.location.href = "/login";
        return;
      }
      throw error; // Re-throw so the calling component knows it failed
    }
  }
  return { callAPI };
}
